import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faPrint, faTrash } from "@fortawesome/free-solid-svg-icons";
import { SettlementPaymentApi, SettlementPaymentDataType } from "@/api/SettlementPaymentApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import { Col, Container, Row } from "reactstrap";
import { SettlementFormDataKeys } from "@/generated/SettlementFormDataKeys";
import { SettlementRelatedAttributes } from "@/formTypes/SettlementDataType";
import { AmplifyFilter, formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { getPaymentMethodLabel } from "@utils/Utils";
import { FetchResponse } from "@/api/ApiClient";
import { INVOICE_PAYMENT_ACTIVE_STATUS, INVOICE_PAYMENT_CANCELLED_STATUS } from "@utils/Constants";
import { SettlementPaymentFormDataKeys } from "@/generated/SettlementPaymentFormDataKeys";
import { SettlementPaymentRelatedAttributes } from "@/formTypes/SettlementPaymentDataType";

const SettlementPaymentIndex = () => {
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { settlementId } = params;
  const [settlement, setSettlement] = useState<SettlementDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentAmount, setPaymentAmount] = useState<Number>(Number(0));
  const [pendingAmount, setPendingAmount] = useState<Number>(Number(0));
  const [paymentList, setPaymentList] = useState<Array<SettlementPaymentDataType>>([]);

  useEffect(() => {
    if (settlementId) {
      const run = async (settlementId: string) => {
        try {
          const settlements = await SettlementApi.get(settlementId, {
            selectionSet: [
              ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
              "currency.*",
              "client.*",
            ]
          });
          setSettlement(settlements);
        } finally {
          setIsLoading(false)
        }
      }
      run(settlementId);
      setIsLoading(true)
    }
  }, [settlementId]);

  useEffect(() => {
    calculatePaymentTotal(paymentList)
  }, [paymentList, settlement])

  const deleteRecord = async (item: SettlementPaymentDataType) => {
    const updateRecord: SettlementPaymentDataType = {
      estado: INVOICE_PAYMENT_CANCELLED_STATUS
    }
    try {
      await SettlementPaymentApi.update(`${item.id}`, updateRecord);
    } catch (err) {
      if (err instanceof Error) {
        showAlert('danger', err.message);
      } else {
        showAlert('success', 'El abono de la factura fue eliminado correctamente');
      }
    }

    modelRef.current?.refetch();
    calculatePaymentTotal(paymentList)
  }

  const calculatePaymentTotal = (paymentList: Array<SettlementPaymentDataType>) => {
    let settlementTotal = Number(0);
    let paymentAmount = Number(0);
    if (paymentList && paymentList.length > 0) {
      paymentAmount = paymentList.filter(p => p.estado == INVOICE_PAYMENT_ACTIVE_STATUS).reduce((a, p) => {
        const amount = Number(p.monto)
        return a + amount;
      }, Number(0))
    }
    setPaymentAmount(paymentAmount)

    if (settlement.total) {
      settlementTotal = settlement.total
    }
    setPendingAmount(settlementTotal - paymentAmount)
  }

  const fetchPayments = async (amplifyClientFilter?: AmplifyFilter, token?: string, limit = 20): Promise<FetchResponse<SettlementPaymentDataType>> => {
    return SettlementPaymentApi.fetchAll(amplifyClientFilter, token, limit,
      [
        ...SettlementPaymentFormDataKeys.filter(k => !SettlementPaymentRelatedAttributes.includes(k)),
        "currency.*",
      ])
  }

  return (
    <PageContainer
      title="Lista Abonos de Liquidacion"
      backUrl="/user/settlements"
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Client:</b></h5>
          </Col>
          <Col>
            <h5><b>{settlement?.client?.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(settlement?.fecha)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Liquidacion:</b>
          </Col>
          <Col>
            {settlement?.numero}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Subtotal:</b>
          </Col>
          <Col>
            {formatMoneyValue(settlement?.subtotal, settlement?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Descuento:</b>
          </Col>
          <Col>
            {formatMoneyValue(settlement?.descuento, settlement?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Impuestos:</b>
          </Col>
          <Col>
            {formatMoneyValue(settlement?.impuestos, settlement?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Total:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(settlement?.total, settlement?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<SettlementPaymentDataType>
        mandatoryFilters={[{ settlementId: { eq: settlementId } }]}
        messages={{
          noResults: "No hay abonos para esta Liquidacion"
        }}
        ref={modelRef}
        onDataFetched={(data) => setPaymentList(data)}
        fetchData={(...args) => fetchPayments(...args)}
        primaryProperties={[
          {
            key: () => "Forma de Pago",
            optionalHighlighted: (val: any, data: SettlementPaymentDataType) => { return data.estado === INVOICE_PAYMENT_CANCELLED_STATUS },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            },
            highlighted: true
          },
          {
            key: (d) => { return getPaymentMethodLabel(d.formaPago) },
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "4" },
            { sm: "8", xs: "8" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              return formatDate(data.created)
            },
            title: "Fecha",
          },
          {
            key: "descripcion",
            title: "Descripcion",
          },
          {
            key: (data) => {
              return formatMoneyValue(data.monto, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "4" },
            { sm: "2", md: "4" }
          ]
        }
        listActions={[
          {
            isVisible: (item: SettlementPaymentDataType) => item.estado != INVOICE_PAYMENT_CANCELLED_STATUS,
            actionTitle: "Eliminar Abono",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Abono",
              confirmMessage: "¿Seguro que desea eliminar esta Abono?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string, item: SettlementPaymentDataType) => { deleteRecord(item) }
          },
          {
            href: `/user/settlements/payments/print/{:id}/${settlementId}`,
            target: '_blank',
            actionTitle: "Imprimir Abono",
            iconProps: { icon: faPrint }
          },
        ]}
      >
      </ModelIndex>
      <hr />
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(settlement?.total, settlement?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Abonos:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(paymentAmount, settlement?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Saldo:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(pendingAmount, settlement?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default SettlementPaymentIndex;
